import { useGet } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Compass, TrendingUp, TrendingDown } from "lucide-react";

const day = (iso) => (iso ? new Date(iso.length === 16 ? iso + ":00Z" : iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const usd = (v) => (v == null ? "—" : `${v >= 0 ? "+" : "−"}$${fmt(Math.abs(v))}`);
const col = (v) => (v == null ? "#9CA3AF" : v > 0 ? "#10B981" : v < 0 ? "#EF4444" : "#9CA3AF");

function Stat({ label, value, color = "#F9FAFB", testId }) {
  return (
    <div className="glass rounded-xl px-3 py-2" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wider text-[#6B7280] font-mono-num">{label}</div>
      <div className="text-xs font-mono-num font-semibold mt-0.5 leading-snug" style={{ color }}>{value}</div>
    </div>
  );
}

export function PositionBias({ refreshKey }) {
  const { data: b } = useGet("/market-drivers/dxy-gold/bias", refreshKey);
  const { data: bt } = useGet("/market-drivers/dxy-gold/crossings-backtest", refreshKey);
  if (!b || b.warming_up) return null;
  const long = b.bias === "LONG";
  const color = b.bias ? (long ? "#10B981" : "#EF4444") : "#9CA3AF";
  const Icon = long ? TrendingUp : TrendingDown;
  const ls = bt?.leg_stats;
  return (
    <section data-testid="position-bias-card" className="glass rounded-3xl p-5 flex flex-col gap-3 relative overflow-hidden">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Compass size={16} className="text-[#FB923C]" />
            <h2 className="font-display text-lg font-bold leading-none">Long-term position · Gold vs DXY on 4H</h2>
          </div>
          <p className="text-[10px] text-[#6B7280] font-mono-num mt-1">Hold from one 4H-chart crossing (60-day rebase) to the next · re-checked after every 4H bar closes · independent of the scalps</p>
        </div>
        {b.bias && (
          <div data-testid="position-bias-direction" className="flex items-center gap-2 px-4 py-1.5 rounded-full font-display font-extrabold text-xl"
            style={{ color, background: `${color}1a`, border: `1px solid ${color}55` }}>
            <Icon size={20} /> {long ? "BUY" : "SELL"} <span className="text-xs font-mono-num font-semibold opacity-80">since {day(b.since)}</span>
          </div>
        )}
      </div>
      <p data-testid="position-bias-reason" className="text-xs text-[#D1D5DB] leading-relaxed">{b.reason}</p>
      {b.bias && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <Stat label="Entry (at crossing)" value={`$${fmt(b.entry)}`} testId="position-entry" />
          <Stat label="Spot now" value={`$${fmt(b.spot)}`} testId="position-spot" />
          <Stat label="Open P/L" value={<>{usd(b.open_pnl_usd)}/oz<br /><span className="opacity-70">{b.open_pnl_pct > 0 ? "+" : ""}{b.open_pnl_pct}%</span></>} color={col(b.open_pnl_usd)} testId="position-pnl" />
          <Stat label="Held" value={`${b.days} day${b.days === 1 ? "" : "s"}`} testId="position-days" />
          <Stat label="Exit" value="Next 4H crossing (alert)" color="#FB923C" testId="position-exit" />
        </div>
      )}
      {ls && (
        <div data-testid="position-legs-stats" className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] leading-relaxed">
          <span className="font-semibold text-[#F9FAFB]">Does holding crossing-to-crossing work? </span>
          Over 2 years of 4H data, {ls.n} completed holds: <span style={{ color: col(ls.total_usd) }}>{usd(ls.total_usd)}/oz total</span>, {ls.win_rate}% of holds profitable, average {usd(ls.avg_usd)}/oz over ~{ls.avg_days} days
          {" "}(longs <span style={{ color: col(ls.long_usd) }}>{usd(ls.long_usd)}</span>, shorts <span style={{ color: col(ls.short_usd) }}>{usd(ls.short_usd)}</span>).
          Simply holding gold over the same period: <span style={{ color: col(ls.buy_hold_usd) }}>{usd(ls.buy_hold_usd)}/oz</span>.
          {ls.short_usd < 0 && ls.long_usd > 0 && " The SELL legs lost money in this bull market — the strategy's edge, if any, is on the long side; treat short legs as a reason to stand aside rather than to short."}
          {ls.total_usd < ls.buy_hold_usd && ls.short_usd >= 0 && " It made money but less than buy-and-hold."}
        </div>
      )}
    </section>
  );
}
